<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';

import Button from '@clovyr/bed/components/controls/Button.vue';
import NostrLogo from '@clovyr/bed/components/icons/NostrLogo.vue';
import { useMatomo } from '@clovyr/bed/composables/useMatomo';
import { getAllQueryParams } from '@clovyr/pollen/http/qs';
import { IncorrectLoginError } from '@clovyr/pollen/pollen/errors';
import { NoPasskeyAvailableError, passkeysAvailable } from '@clovyr/pollen/user';
import { removeWhitespace } from '@clovyr/pollen/util/string';

import { useNostr } from '@/composables/useNostr';
import { usePayments } from '@/composables/usePayments';
import { usePollenStore } from '@/stores/pollen_store';

import Banner from '../../../components/blocks/Banner.vue';
import FormField from '../../../components/blocks/FormField.vue';
import PasswordFormField from '../../../components/blocks/PasswordFormField.vue';
import FormWrapper from '../../../components/forms/FormWrapper.vue';
import { ShowAuthForm } from '../types';

import { validateSecretKey } from './util';

export type LoginProps = {
  isShared?: boolean;
  isPubFlow?: boolean;
  text?: string;
};

const props = defineProps<LoginProps>();

const emit = defineEmits<{
  (e: 'authForm', form: string): void;
  (e: 'close'): void;
}>();

const pollenStore = usePollenStore();
const { pollen } = pollenStore;

const { isNostrAvailable } = useNostr();

const query = getAllQueryParams();
const emailParam = ref(query.get('email') || '');
const secretKeyParam = ref(query.get('secret') || '');
const passkeyError = ref('');
const supportsPasskeys = computed(() => passkeysAvailable());
const usePassword = ref(false);
const submitting = ref(false);
const needsRecoveryKey = ref(false);

function showPassword() {
  usePassword.value = true;
  needsRecoveryKey.value = false;
  passkeyError.value = '';
}

function toggleLoginMethod() {
  if (!usePassword.value) return showPassword();
  usePassword.value = false;
  needsRecoveryKey.value = false;
  userHasSecretKey.value = true;
  passkeyError.value = '';
}

const handleSignup = (form?: ShowAuthForm) => {
  emit('authForm', form || ShowAuthForm.Signup);
};

const userHasSecretKey = ref(true);

const toggleSecretKey = () => {
  userHasSecretKey.value = !userHasSecretKey.value;
};

// const handleForgotPassword = () => {
//   emit('authForm', 'showResetPassword');
// };
const route = useRoute();
const router = useRouter();

/**
 * Load user-specific data post-login
 */
const doPostLogin = async () => {
  return usePayments().refreshSubscription();
};

const doRedirect = async () => {
  let redirect = '';
  if (props.isShared) {
    redirect = 'Library';
  } else if (route.query.redirect || route.query.redir) {
    redirect = (route.query.redirect || route.query.redir)!.toString();
  }
  if (!redirect) {
    redirect = '/';
  }
  if (redirect.startsWith('http')) {
    window.location.href = redirect;
  }
  await router.push(redirect);
};

const onSubmit = async (values, actions) => {
  if (submitting.value) return;
  emailParam.value = values.email.trim();
  if (!usePassword.value) {
    if (!supportsPasskeys.value) {
      showPassword();
      return;
    }
    await loginWithPasskey();
    return;
  }
  submitting.value = true;
  const matomo = useMatomo();
  try {
    values.secretKey &&= removeWhitespace(values.secretKey);
    if (userHasSecretKey.value && values.secretKey) {
      await pollen.logIn(values.email, values.password, values.secretKey);
      matomo.trackEvent('Auth', 'Login', 'Password & Secret');
    } else {
      await pollen.logIn(values.email, values.password);
      matomo.trackEvent('Auth', 'Login', 'Password Only');
    }
    await doPostLogin();
    if (!props.isPubFlow) {
      await doRedirect();
    }
    emit('close');
  } catch (err) {
    if (err instanceof IncorrectLoginError) {
      actions.setErrors({
        email: ' ', // mark the field as invalid with no message
        password: 'Enter a valid email and password.',
      });
      return;
    }

    throw err;
  } finally {
    submitting.value = false;
  }
};

const loginWithNostr = async () => {
  await pollen.logInWithNostr();
  const matomo = useMatomo();
  matomo.trackEvent('Auth', 'Login', 'Nostr');
  await doPostLogin();
  await doRedirect();
  emit('close');
};

const loginWithPasskey = async () => {
  submitting.value = true;
  passkeyError.value = '';
  const email = emailParam.value.trim();
  if (!email) {
    passkeyError.value = 'Enter your email before using a passkey.';
    submitting.value = false;
    return;
  }

  const recoveryKey = userHasSecretKey.value
    ? removeWhitespace(secretKeyParam.value || '')
    : undefined;

  try {
    await pollen.logInWithPasskey(email, recoveryKey || undefined);
    const matomo = useMatomo();
    matomo.trackEvent('Auth', 'Login', recoveryKey ? 'Passkey & Recovery Key' : 'Passkey Only');
    await doPostLogin();
    if (!props.isPubFlow) {
      await doRedirect();
    }
    emit('close');
  } catch (err) {
    if (err instanceof NoPasskeyAvailableError) {
      showPassword();
      return;
    }
    if (err instanceof Error && err.message === 'passkey vault is not enrolled on this device') {
      needsRecoveryKey.value = true;
      passkeyError.value =
        'Your passkey verified your account. Enter your secret key to unlock your library on this device.';
      return;
    }
    if (err instanceof IncorrectLoginError) {
      passkeyError.value = 'Passkey login failed for that email.';
      return;
    }
    passkeyError.value = 'Passkey login failed. Please try again.';
    console.warn(err);
  } finally {
    submitting.value = false;
  }
};

const newDeviceSchema = z.object({
  secretKey: validateSecretKey,
});

const normalSchema = z.object({
  email: z.string().email({ message: 'Enter a valid email address.' }),
  password: z.string().min(8, { message: 'Password must be at least 8 characters long.' }),
});

const regularSchema = toTypedSchema(normalSchema);
const mergedSchema = toTypedSchema(normalSchema.merge(newDeviceSchema));
const emailOnlySchema = z.object({ email: normalSchema.shape.email });
const activeSchema = computed(() => {
  if (usePassword.value) return userHasSecretKey.value ? mergedSchema : regularSchema;
  return toTypedSchema(
    needsRecoveryKey.value ? emailOnlySchema.merge(newDeviceSchema) : emailOnlySchema,
  );
});
</script>

<template>
  <div class="form-wrapper form-auth">
    <template v-if="userHasSecretKey || !usePassword">
      <div class="form-heading">Log in</div>
      <div v-if="text" class="form-description sub-heading">{{ text }}<br /><br /></div>
      <banner v-if="isPubFlow" status="info" class="banner">
        In order to access the Publisher Portal, you must first log in or register for a Clovyr
        account.
      </banner>
      <div class="form-description sub-heading">Enter your email to continue to your account.</div>
    </template>

    <template v-else>
      <div>
        <p class="form-heading">Don't have your secret key?</p>
        <p class="form-heading">No Problem.</p>
        <p v-if="text" class="form-description sub-heading">{{ text }}<br /><br /></p>
        <p class="form-description sub-heading">
          You can still access most of Clovyr without it, but you'll need your secret key to connect
          to your library of apps.
        </p>
      </div>
    </template>

    <div class="form">
      <FormWrapper
        :submit-label="submitting ? 'Signing in…' : usePassword ? 'Log in' : 'Continue'"
        :validation-schema="activeSchema"
        :disabled="submitting"
        @submit="onSubmit"
      >
        <template #fields>
          <FormField
            name="email"
            label="Email"
            type="email"
            autocomplete="username"
            shape="cubed"
            v-model="emailParam"
          />

          <PasswordFormField
            v-if="usePassword"
            name="password"
            label="Password"
            type="password"
            shape="cubed"
            reveal
          />

          <PasswordFormField
            v-if="(usePassword && userHasSecretKey) || needsRecoveryKey"
            name="secretKey"
            label="Secret Key"
            type="password"
            shape="cubed"
            v-model="secretKeyParam"
            reveal
          >
            <template #tooltip>
              Your Secret Key can be found in your <a href="/">Recovery Kit</a>. Clovyr does not
              have access to your Secret Key and cannot help recover it if you lose it.
            </template>
          </PasswordFormField>
        </template>
      </FormWrapper>
      <div v-if="supportsPasskeys" class="form-callout">
        <button
          type="button"
          class="auth-method-link button-inline button-primary"
          :disabled="submitting"
          @click="toggleLoginMethod"
        >
          {{ usePassword ? 'Use a passkey instead' : 'Use password instead' }}
        </button>
      </div>

      <Banner status="critical" v-if="passkeyError" class="passkey-error">
        {{ passkeyError }}
      </Banner>

      <Button
        v-if="isNostrAvailable"
        class="button nostr-button"
        label="Log In With Nostr"
        @click="loginWithNostr"
      >
        <NostrLogo />
      </Button>

      <div class="form-callout">
        <!-- <span v-if="false" class="text--underline text--link" @click="handleForgotPassword"
          >Forgot password?</span
        > -->
        <span>Need an account?</span>
        <span class="button-inline button-primary" @click="handleSignup()">Sign up</span>
      </div>
      <div v-if="usePassword" class="form-callout">
        <div v-if="userHasSecretKey">
          <span>Don't have your secret key?</span>
          <span class="button-inline button-primary" @click="toggleSecretKey">Click Here</span>
        </div>
        <div v-else>
          <span>Found your secret key?</span>
          <span class="button-inline button-primary" @click="toggleSecretKey">Click Here</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';

.form-wrapper {
  max-width: 30rem;
}
.form-heading {
  .subtext {
    color: var(--Grey-1, #847398);
    font-size: 15px;
    line-height: 20px; /* 133.333% */
    letter-spacing: -0.15px;
    margin-top: 0.5rem;
  }
}
.sub-heading {
  max-width: 35rem;
}
.form-callout {
  > * {
    margin-left: 0.25rem;
  }
  div > * {
    margin-left: 0.25rem;
  }
}
.button {
  height: 2.85rem;
}
.nostr-button {
  background-color: #995ada;
  color: white;
  width: 100%;
  margin-top: 1rem;

  svg {
    width: 3rem;
    height: 2rem;
  }
}
.nostr-button:hover {
  background-color: colors.color(active, primary);
}

.auth-method-link {
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.passkey-error {
  margin-top: 1rem;
}
</style>
